// Phase 4a — Visual Identity (read-only).
// Phase 4b — adds inline edit: logo URL, color swatches, font family.
// 2026-07-14 — absorbs the Video Theme controls from the Video card:
// operators can generate/edit the detailed theme JSON here alongside
// the basic identity fields. Theme drives the shared canonical scripts.

import { useCallback, useEffect, useState } from 'react';
import {
  Card, CardBody, HStack, VStack, Text, Box, Image, Heading, Input,
  SimpleGrid, IconButton, Icon,
  Button, Badge, Textarea, Collapse, useToast
} from '@chakra-ui/react';
import { apiJson } from '../../auth/apiFetch';
import type { Brand } from './types';
import type { BrandEdit } from './useBrandEdit';
import { safeHttpUrl } from '../../lib/safeHref';

export function VisualIdentityCard({ brand, edit }: { brand: Brand; edit: BrandEdit }) {
  const isEditing = edit.isEditing;

  const logoUrl   = (edit.valueOf('logoUrl')        as string | null) ?? '';
  const logoSrc   = safeHttpUrl(logoUrl);
  const primary   = (edit.valueOf('primaryColor')   as string | null) ?? '';
  const secondary = (edit.valueOf('secondaryColor') as string | null) ?? '';
  const accent    = (edit.valueOf('accentColor')    as string | null) ?? '';
  const fontColor = (edit.valueOf('fontColor')      as string | null) ?? '';
  const fontFam   = (edit.valueOf('fontFamily')     as string | null) ?? '';

  const swatches = [
    { label: 'Primary',   hex: primary,   set: (v: string) => edit.setField('primaryColor',   v || null) },
    { label: 'Secondary', hex: secondary, set: (v: string) => edit.setField('secondaryColor', v || null) },
    { label: 'Accent',    hex: accent,    set: (v: string) => edit.setField('accentColor',    v || null) },
    { label: 'Text',      hex: fontColor, set: (v: string) => edit.setField('fontColor',      v || null) }
  ];

  return (
    <Card variant="outline" h="100%">
      <CardBody>
        <Heading size="sm" textTransform="uppercase" letterSpacing="0.04em" mb={4}>Visual Identity</Heading>

        <VStack align="stretch" spacing={4}>
          {/* Logo + URL */}
          <Box>
            <Text fontSize="xs" color="brand.muted" mb={1.5}>Logo</Text>
            <HStack align="center" spacing={3}>
              <Box
                w="56px" h="56px"
                borderRadius="md"
                bg={primary || 'gray.100'}
                border="1px solid"
                borderColor="brand.border"
                display="flex" alignItems="center" justifyContent="center"
                overflow="hidden"
                flexShrink={0}
              >
                {logoSrc
                  ? <Image src={logoSrc} alt={brand.name} maxW="100%" maxH="100%" objectFit="contain" />
                  : <Text color="white" fontSize="lg" fontWeight="800" opacity={0.6}>{brand.name?.charAt(0).toUpperCase() || '?'}</Text>}
              </Box>
              <Input
                size="sm"
                value={logoUrl}
                onChange={e => edit.setField('logoUrl', e.target.value || null)}
                isReadOnly={!isEditing}
                fontFamily="mono"
                fontSize="11px"
                placeholder={isEditing ? 'https://cdn.../logo.png' : '(no logo)'}
              />
            </HStack>
          </Box>

          {/* Color swatches */}
          <SimpleGrid columns={4} spacing={2}>
            {swatches.map(s => (
              <Swatch
                key={s.label}
                label={s.label}
                hex={s.hex}
                isEditing={isEditing}
                onChange={s.set}
              />
            ))}
          </SimpleGrid>

          {/* Font family */}
          <Box>
            <Text fontSize="xs" color="brand.muted" mb={1.5}>Font Family</Text>
            <Input
              size="sm"
              value={fontFam}
              onChange={e => edit.setField('fontFamily', e.target.value || null)}
              isReadOnly={!isEditing}
              placeholder={isEditing ? 'e.g. Oswald, Inter, Playfair Display' : '(none)'}
              fontFamily={fontFam || undefined}
            />
            {brand.fontSource && !isEditing && (
              <Text fontSize="9px" color="brand.muted" mt={1}>source: {brand.fontSource}</Text>
            )}
            {isEditing && (
              <Text fontSize="9px" color="brand.muted" mt={1}>
                Saving sets fontSource to "curated" — protects from re-enrichment.
              </Text>
            )}
          </Box>

          {/* Video Theme — detailed renderer knobs for the canonical
              scripts. Manages Brand.styleTheme directly (bypasses the
              useBrandEdit draft since theme is its own atomic Mixed
              blob and doesn't participate in the SaveBar dirty flow). */}
          <VideoThemeSection brand={brand} />
        </VStack>
      </CardBody>
    </Card>
  );
}

// ── Video Theme subsection ─────────────────────────────────────────
//
// Detailed color + font-family knobs consumed by the shared canonical
// scripts. Editing/generating flows through the same POST
// /generate-script + PATCH /:id endpoints the Video card previously
// used. Preview lives on the Video card; save here, preview there.

function VideoThemeSection({ brand }: { brand: Brand }) {
  const toast = useToast();
  const [expanded, setExpanded] = useState(false);
  const [themeText, setThemeText] = useState('');
  const [themeDirty, setThemeDirty] = useState(false);
  const [themeParseError, setThemeParseError] = useState<string | null>(null);
  const [themeSaving, setThemeSaving] = useState(false);
  const [themeClearing, setThemeClearing] = useState(false);
  const [genDirection, setGenDirection] = useState('');
  const [generating, setGenerating] = useState(false);

  // Load theme from the brand doc. brand.styleTheme is populated by
  // GET /api/brand/:id, so no separate fetch needed.
  useEffect(() => {
    setThemeText(brand.styleTheme ? JSON.stringify(brand.styleTheme, null, 2) : '');
    setThemeDirty(false);
    setThemeParseError(null);
  }, [brand._id, brand.styleTheme]);

  const handleThemeChange = (v: string) => {
    setThemeText(v);
    setThemeDirty(true);
    try {
      if (v.trim()) JSON.parse(v);
      setThemeParseError(null);
    } catch (err) {
      setThemeParseError((err as Error).message);
    }
  };

  const handleThemeSave = async () => {
    if (themeParseError) {
      toast({ status: 'error', title: 'Invalid JSON', description: themeParseError });
      return;
    }
    let parsed: unknown;
    try {
      parsed = themeText.trim() ? JSON.parse(themeText) : null;
    } catch (err) {
      toast({ status: 'error', title: 'Invalid JSON', description: (err as Error).message });
      return;
    }
    setThemeSaving(true);
    try {
      await apiJson(`/api/brand/${brand._id}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ styleTheme: parsed })
      });
      toast({ status: 'success', title: 'Video theme saved' });
      setThemeDirty(false);
      // Note: no refresh() call here — brand prop will update on next
      // load; the useEffect above resyncs textarea if needed.
    } catch (err) {
      toast({ status: 'error', title: 'Save failed', description: (err as Error).message });
    } finally {
      setThemeSaving(false);
    }
  };

  const handleThemeClear = async () => {
    setThemeClearing(true);
    try {
      await apiJson(`/api/brand/${brand._id}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ styleTheme: null })
      });
      toast({ status: 'success', title: 'Video theme cleared' });
      setThemeText('');
      setThemeDirty(false);
    } catch (err) {
      toast({ status: 'error', title: 'Clear failed', description: (err as Error).message });
    } finally {
      setThemeClearing(false);
    }
  };

  const handleGenerateTheme = useCallback(async () => {
    setGenerating(true);
    try {
      // Kick off async job — Claude takes 30-90s (past Netlify's ~26s
      // proxy timeout), so we poll instead of awaiting inline.
      const kick = await apiJson<{ jobId: string }>(
        `/api/brand/${brand._id}/generate-script`,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ direction: genDirection.trim() || undefined })
          // mode defaults to 'theme' on the backend
        }
      );
      await pollGenerateTheme(kick.jobId);
    } catch (err) {
      toast({ status: 'error', title: 'Generation failed', description: (err as Error).message });
      setGenerating(false);
    }
  }, [brand._id, genDirection, toast]);

  const pollGenerateTheme = (jobId: string): Promise<void> => {
    const INTERVAL_MS = 3000;
    const TIMEOUT_MS  = 3 * 60 * 1000;
    const startedAt = Date.now();
    return new Promise<void>((resolve) => {
      const tick = async () => {
        try {
          const s = await apiJson<{
            status: 'pending' | 'done' | 'failed';
            theme?: Record<string, unknown>;
            error?: string;
            model?: string;
            elapsedMs?: number;
          }>(`/api/brand/${brand._id}/generate-script/${jobId}`);
          if (s.status === 'done' && s.theme) {
            setThemeText(JSON.stringify(s.theme, null, 2));
            setThemeDirty(true);
            setThemeParseError(null);
            setGenerating(false);
            toast({
              status: 'success',
              title: 'Theme generated',
              description: `${s.model || 'Claude'} · ${((s.elapsedMs || 0) / 1000).toFixed(1)}s. Review + Save to persist.`
            });
            resolve();
            return;
          }
          if (s.status === 'failed') {
            setGenerating(false);
            toast({ status: 'error', title: 'Generation failed', description: s.error || 'Unknown error' });
            resolve();
            return;
          }
        } catch { /* transient — retry */ }
        if (Date.now() - startedAt > TIMEOUT_MS) {
          setGenerating(false);
          toast({ status: 'warning', title: 'Generation still running', description: 'Taking longer than expected. Refresh and check back.' });
          resolve();
          return;
        }
        setTimeout(tick, INTERVAL_MS);
      };
      setTimeout(tick, INTERVAL_MS);
    });
  };

  const themeSet = !!brand.styleTheme && Object.keys(brand.styleTheme || {}).length > 0;

  return (
    <Box borderTopWidth="1px" borderColor="brand.border" pt={4}>
      <HStack justify="space-between" mb={2}>
        <HStack spacing={2}>
          <Text fontSize="xs" color="brand.muted" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600">
            Video theme
          </Text>
          <Badge fontSize="9px" colorScheme={themeSet ? 'purple' : 'gray'}>
            {themeSet ? 'Custom' : 'Defaults'}
          </Badge>
        </HStack>
        <Button size="xs" variant="ghost" onClick={() => setExpanded(v => !v)}>
          {expanded ? 'Hide' : 'Edit'}
        </Button>
      </HStack>
      <Text fontSize="10px" color="brand.muted" mb={2}>
        Detailed color + font knobs that overlay into the shared canonical video scripts. Preview after saving via the Video card below.
      </Text>
      <Collapse in={expanded} animateOpacity>
        <VStack align="stretch" spacing={3} pt={2}>
          <Box borderWidth="1px" borderRadius="md" borderColor="brand.border" p={3}>
            <VStack align="stretch" spacing={2}>
              <Text fontSize="xs" color="brand.muted">
                Generate a theme with Claude from the brand's tagline / tone / colors.
              </Text>
              <Textarea
                value={genDirection}
                onChange={e => setGenDirection(e.target.value)}
                rows={2}
                fontSize="xs"
                placeholder="Optional direction — e.g. 'muted earth tones, serif-forward, gold accents'"
              />
              <HStack justify="flex-end">
                <Button size="sm" variant="brand" onClick={handleGenerateTheme} isLoading={generating} loadingText="Generating (~30s)">
                  Generate with AI
                </Button>
              </HStack>
            </VStack>
          </Box>

          <Textarea
            value={themeText}
            onChange={e => handleThemeChange(e.target.value)}
            fontFamily="mono"
            fontSize="xs"
            rows={16}
            spellCheck={false}
            placeholder='{ "sansFontFamily": "Inter", "ctaBgColor": [70, 120, 62], ... }'
          />
          {themeParseError && (
            <Text fontSize="xs" color="red.500">JSON error: {themeParseError}</Text>
          )}
          <HStack justify="space-between" wrap="wrap" spacing={2}>
            <Button size="sm" variant="ghost" colorScheme="red" onClick={handleThemeClear} isLoading={themeClearing} isDisabled={!themeSet}>
              Clear theme
            </Button>
            <Button size="sm" variant="brand" onClick={handleThemeSave} isLoading={themeSaving} isDisabled={!themeDirty || !!themeParseError}>
              Save theme
            </Button>
          </HStack>
        </VStack>
      </Collapse>
    </Box>
  );
}

function Swatch({
  label, hex, isEditing, onChange
}: {
  label: string;
  hex: string;
  isEditing: boolean;
  onChange: (value: string) => void;
}) {
  const valid = /^#[0-9a-fA-F]{6}$/.test(hex);
  const bgValue = valid ? hex : 'gray.100';
  return (
    <VStack spacing={1.5} align="center">
      <Box position="relative" w="100%" h="40px" borderRadius="md" border="1px solid" borderColor="brand.border" bg={bgValue} overflow="hidden">
        {isEditing && (
          <>
            <input
              type="color"
              value={valid ? hex : '#ffffff'}
              onChange={e => onChange(e.target.value.toUpperCase())}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none', padding: 0, cursor: 'pointer', opacity: 0 }}
              aria-label={`${label} color picker`}
            />
            <PaintIcon />
          </>
        )}
      </Box>
      <Text fontSize="10px" color="brand.muted" textTransform="uppercase" letterSpacing="0.04em">{label}</Text>
      {isEditing ? (
        <HStack spacing={1}>
          <Input
            size="xs"
            value={hex}
            onChange={e => {
              const next = e.target.value.toUpperCase();
              if (next === '' || /^#[0-9a-fA-F]{0,6}$/.test(next)) onChange(next);
            }}
            placeholder="#FFFFFF"
            fontFamily="mono"
            fontSize="10px"
            textAlign="center"
            maxLength={7}
          />
          {hex && (
            <IconButton
              size="xs"
              aria-label={`Clear ${label}`}
              variant="ghost"
              minW="20px"
              h="20px"
              onClick={() => onChange('')}
              icon={<Box as="span" fontSize="11px" lineHeight={1}>×</Box>}
            />
          )}
        </HStack>
      ) : (
        <Text fontSize="10px" color="brand.ink" fontFamily="mono">{valid ? hex.toUpperCase() : '—'}</Text>
      )}
    </VStack>
  );
}

function PaintIcon() {
  return (
    <Box position="absolute" top={1} right={1} pointerEvents="none">
      <Icon viewBox="0 0 24 24" w="12px" h="12px" color="whiteAlpha.700">
        <path fill="currentColor" d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
      </Icon>
    </Box>
  );
}
